import { useState, useEffect } from 'react';
import { ShoppingCart, Search, Percent, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import ContentEditor from '@/components/admin/ContentEditor';
import { useAuth } from '@/contexts/AuthContext';

const WhatWeDo = () => {
  const { isAdmin } = useAuth();
  const [steps, setSteps] = useState([
    {
      number: 1,
      title: "1. רוכשים עודפי פרימיום ישירות מיבואנים מובילים",
      icon: ShoppingCart,
      color: "bg-[#81ba1e] text-white border-[#81ba1e]"
    },
    {
      number: 2,
      title: "2. בודקים ומקטלגים כל פריט לפני העלאתו לאתר",
      icon: Search,
      color: "bg-[#81ba1e] text-white border-[#81ba1e]"
    },
    {
      number: 3,
      title: "3. דואגים לכם להנחות של עשרות אחוזים בזכות הסכמים בלעדיים",
      icon: Percent,
      color: "bg-[#81ba1e] text-white border-[#81ba1e]"
    },
    {
      number: 4,
      title: "4. מעבירים את כל החסכון אליכם, בלי אותיות קטנות",
      icon: Heart,
      color: "bg-[#81ba1e] text-white border-[#81ba1e]"
    }
  ]);

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('page_contents')
        .select('*')
        .eq('page_name', 'homepage')
        .eq('section_name', 'WhatWeDo')
        .order('order_index');

      if (error) throw error;

      if (data && data.length > 0) {
        const contentSteps = data.map((item, index) => {
          const parsedContent = JSON.parse(item.content);
          const iconMap: Record<string, any> = {
            ShoppingCart,
            Search,
            Percent,
            Heart
          };

          return {
            number: index + 1,
            title: parsedContent.title,
            icon: iconMap[parsedContent.icon] || ShoppingCart,
            color: "bg-[#81ba1e] text-white border-[#81ba1e]"
          };
        });
        setSteps(contentSteps);
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-background via-lime/5 to-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground hebrew-heading">
              מה אנחנו עושים?
            </h2>
            {isAdmin && (
              <ContentEditor componentType="WhatWeDo" onSave={loadContent} />
            )}
          </div>

          {/* Desktop - Horizontal Cards with Numbers */}
          <div className="hidden md:block">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={step.number} className="relative">
                    <div className="bg-card rounded-xl p-6 shadow-elegant border border-border hover:shadow-lg transition-shadow h-full">
                      <div className="flex items-center gap-4 text-right h-full">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 ${step.color}`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <p className="text-lg text-foreground font-medium hebrew-body leading-relaxed flex-1">
                          {step.title}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile - Stacked Cards with Colored Numbers */}
          <div className="md:hidden space-y-6">
            {steps.map((step) => {
              const IconComponent = step.icon;
              return (
                <div key={step.number} className="relative">
                  <div className="bg-card rounded-xl p-6 shadow-elegant border border-border">
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 ${step.color}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <p className="text-lg text-foreground font-medium hebrew-body leading-relaxed text-right flex-1">
                        {step.title}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;